const DEFAULT_TIMEZONE = "Asia/Kolkata";

export function getRestaurantTimezone(timezone) {
  const tz = (timezone || "").toString().trim();
  return tz || DEFAULT_TIMEZONE;
}

/** YYYY-MM-DD for an instant in the restaurant timezone */
export function getDateStringInTimezone(dateInput, timezone = DEFAULT_TIMEZONE) {
  const d = dateInput instanceof Date ? dateInput : new Date(dateInput);
  if (Number.isNaN(d.getTime())) return "";

  const tz = getRestaurantTimezone(timezone);
  try {
    return new Intl.DateTimeFormat("en-CA", {
      timeZone: tz,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(d);
  } catch {
    return d.toISOString().slice(0, 10);
  }
}

export function getRestaurantTodayDate(timezone = DEFAULT_TIMEZONE) {
  return getDateStringInTimezone(new Date(), timezone);
}

export function getRestaurantCurrentTime(timezone = DEFAULT_TIMEZONE) {
  const d = new Date();
  const tz = getRestaurantTimezone(timezone);

  try {
    const parts = new Intl.DateTimeFormat("en-GB", {
      timeZone: tz,
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).formatToParts(d);

    const hour = parts.find((part) => part.type === "hour")?.value || "00";
    const minute = parts.find((part) => part.type === "minute")?.value || "00";
    return `${hour}:${minute}`;
  } catch {
    const localHour = String(d.getHours()).padStart(2, "0");
    const localMinute = String(d.getMinutes()).padStart(2, "0");
    return `${localHour}:${localMinute}`;
  }
}

/** Build a local Date for date pickers from YYYY-MM-DD (calendar day as shown) */
export function dateFromYmd(yyyyMmDd) {
  if (!yyyyMmDd || typeof yyyyMmDd !== "string") return new Date();
  const [y, m, d] = yyyyMmDd.split("-").map(Number);
  if (!y || !m || !d) return new Date();
  return new Date(y, m - 1, d);
}

/** Calendar YYYY-MM-DD from a date-picker value (the day the user selected) */
export function formatDateForAPI(date) {
  if (!date) return "";
  if (typeof date === "string" && /^\d{4}-\d{2}-\d{2}$/.test(date)) return date;

  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return "";

  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function formatDateTimeInTimezone(
  dateInput,
  timezone = DEFAULT_TIMEZONE,
  locale = "en-GB",
  options = {},
) {
  const d = dateInput instanceof Date ? dateInput : new Date(dateInput);
  if (Number.isNaN(d.getTime())) return "-";

  const tz = getRestaurantTimezone(timezone);
  try {
    return d.toLocaleString(locale, {
      timeZone: tz,
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
      ...options,
    });
  } catch {
    return d.toLocaleString(locale);
  }
}

export function formatDateInTimezone(
  dateInput,
  timezone = DEFAULT_TIMEZONE,
  locale = "en-IN",
  options = {},
) {
  const d = dateInput instanceof Date ? dateInput : new Date(dateInput);
  if (Number.isNaN(d.getTime())) return "-";

  const tz = getRestaurantTimezone(timezone);
  try {
    return d.toLocaleDateString(locale, {
      timeZone: tz,
      ...options,
    });
  } catch {
    return d.toLocaleDateString(locale, options);
  }
}
